import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { CalendarClock, Dumbbell, Moon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_START_H = 7;
const DAY_END_H = 21;
const MIN_SLOT_MIN = 45;
const DEFAULT_EVENT_MIN = 60;

function roundUpQuarter(ts) {
    const q = 15 * 60000;
    return Math.ceil(ts / q) * q;
}

export function computeDayPlan(events, now = new Date()) {
    const isToday = (d) => d.toDateString() === now.toDateString();
    const timed = events
        .filter(ev => !ev.all_day && isToday(new Date(ev.start)))
        .map(ev => {
            const s = new Date(ev.start);
            const e = ev.end ? new Date(ev.end) : new Date(s.getTime() + DEFAULT_EVENT_MIN * 60000);
            return { ...ev, s, e: e > s ? e : new Date(s.getTime() + DEFAULT_EVENT_MIN * 60000) };
        })
        .sort((a, b) => a.s - b.s);

    const busy = [];
    for (const ev of timed) {
        const s = ev.s.getTime(), e = ev.e.getTime();
        if (busy.length && s <= busy[busy.length - 1][1]) {
            busy[busy.length - 1][1] = Math.max(busy[busy.length - 1][1], e);
        } else {
            busy.push([s, e]);
        }
    }

    const dayStart = new Date(now); dayStart.setHours(DAY_START_H, 0, 0, 0);
    const dayEnd = new Date(now); dayEnd.setHours(DAY_END_H, 0, 0, 0);
    let cursor = Math.max(dayStart.getTime(), now.getTime());
    const slots = [];
    for (const [s, e] of busy) {
        if (s > cursor) {
            const end = Math.min(s, dayEnd.getTime());
            if (end - cursor >= MIN_SLOT_MIN * 60000) slots.push([cursor, end]);
        }
        cursor = Math.max(cursor, e);
    }
    if (dayEnd.getTime() - cursor >= MIN_SLOT_MIN * 60000) slots.push([cursor, dayEnd.getTime()]);

    // Prefer the earliest slot with >=60min (workout between meetings);
    // otherwise fall back to the largest remaining slot.
    let best = null;
    for (const sl of slots) {
        if (sl[1] - sl[0] >= 60 * 60000) { best = sl; break; }
    }
    if (!best) {
        for (const sl of slots) {
            if (!best || sl[1] - sl[0] > best[1] - best[0]) best = sl;
        }
    }
    if (best) {
        const start = roundUpQuarter(best[0]);
        best = start < best[1] - 30 * 60000 ? [start, best[1]] : best;
    }
    const lastEnd = busy.length ? new Date(busy[busy.length - 1][1]) : null;
    return { timed, slots, best, lastEnd };
}

export function DayPlanCard() {
    const { language } = useLanguage();
    const de = language === 'de';
    const [events, setEvents] = useState(null);

    useEffect(() => {
        axios.get(`${API}/calendar/upcoming`)
            .then(res => setEvents(res.data?.configured ? (res.data.events || []) : null))
            .catch(() => setEvents(null));
    }, []);

    if (!events) return null;
    const { timed, best, lastEnd } = computeDayPlan(events);
    if (timed.length === 0) return null;

    const fmt = (d) => d.toLocaleTimeString(de ? 'de-CH' : 'en-GB', { hour: '2-digit', minute: '2-digit' });
    const slotStart = best ? fmt(new Date(best[0])) : null;
    const slotMin = best ? Math.round((best[1] - best[0]) / 60000) : 0;
    const slotDur = slotMin >= 90 ? `${Math.floor(slotMin / 60)}:${String(slotMin % 60).padStart(2, '0')} h` : `${slotMin} Min`;

    return (
        <Card className="border-violet-200 bg-violet-50/40" data-testid="day-plan-card">
            <CardContent className="p-4 space-y-2.5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CalendarClock className="w-5 h-5 text-violet-600" />
                        <span className="text-sm font-semibold" data-testid="day-plan-title">
                            {de ? 'Dein Tag heute' : 'Your day today'}
                        </span>
                    </div>
                    <span className="text-xs font-medium text-violet-700" data-testid="day-plan-event-count">
                        {timed.length} {de ? (timed.length === 1 ? 'Termin' : 'Termine') : (timed.length === 1 ? 'event' : 'events')}
                    </span>
                </div>

                <div className="space-y-1" data-testid="day-plan-event-list">
                    {timed.slice(0, 4).map((ev) => (
                        <div key={`${ev.start}-${ev.summary}`} className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground shrink-0 tabular-nums w-[88px]">
                                {fmt(ev.s)}–{fmt(ev.e)}
                            </span>
                            <span className="truncate font-medium">{ev.summary}</span>
                        </div>
                    ))}
                    {timed.length > 4 && (
                        <p className="text-[10px] text-muted-foreground">+ {timed.length - 4} {de ? 'weitere' : 'more'}</p>
                    )}
                </div>

                <div className="flex items-start gap-2.5 rounded-xl bg-white/80 border border-violet-200 px-3 py-2" data-testid="day-plan-suggestion">
                    <img src="/nanouk/energy_fit.png" alt="Nanouk" className="w-10 h-10 object-contain shrink-0" />
                    <p className="text-xs leading-snug pt-0.5">
                        {best ? (
                            de
                                ? <><Dumbbell className="w-3.5 h-3.5 inline text-violet-600 mr-0.5" /> {lastEnd ? <>Bis {fmt(lastEnd)} Uhr bist du eingespannt. </> : null}<strong>{slotStart} Uhr</strong> wäre perfekt für dein Workout – da hast du <strong>{slotDur}</strong> frei! 💪</>
                                : <><Dumbbell className="w-3.5 h-3.5 inline text-violet-600 mr-0.5" /> {lastEnd ? <>You're booked until {fmt(lastEnd)}. </> : null}<strong>{slotStart}</strong> would be perfect for your workout – you've got <strong>{slotDur}</strong> free! 💪</>
                        ) : (
                            de
                                ? <><Moon className="w-3.5 h-3.5 inline text-violet-600 mr-0.5" /> Heute ist dicht gepackt{lastEnd ? ` – frei erst ab ${fmt(lastEnd)} Uhr` : ''}. Wie wär's mit einem Abendspaziergang? 🌙</>
                                : <><Moon className="w-3.5 h-3.5 inline text-violet-600 mr-0.5" /> Today is packed{lastEnd ? ` – free after ${fmt(lastEnd)}` : ''}. How about an evening walk? 🌙</>
                        )}
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
